import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthNew } from 'src/components/AuthNew';
import {
  getClientData,
  getClientPhone,
  getClientUnconfirmedPhone,
  getConfirmedAt,
} from 'src/store/client/selectors';
import { getIsMobile, getIsWhitelabel } from 'src/store/view/selectors';
import { TestimonialDone } from 'src/routes/TestimonialsForm/components/TestimonialDone';
import { TestimonialsAuthWrapper } from 'src/routes/TestimonialsForm/components/TestimonialsAuth';
import { CommentLoader } from 'src/routes/TestimonialsForm/components/CommentLoader';
import { AuthFormType, PageType } from 'src/components/AuthNew/types';
import { css } from 'styled-components';
import { useTestimonialsFormCache } from 'src/routes/TestimonialsForm/hooks/useTestimonialsFormCache';
import { useTestimonialsFormTypes } from 'src/routes/TestimonialsForm/hooks/useTestimonialsFormTypes';
import { logInAction, setClientConfirmPhone } from 'src/store/client/actions';
import { getClientType } from 'src/components/AuthNew/utils/getClientType';
import { TestimonialsFormInner } from './components/TestimonialsFormInner';
import { TestimonialsForm } from './components/TestimonialsForm';
import { TestimonialsFormTypes } from './types';
import { useTestimonialsCreate, useTestimonialsLink } from './hooks';
import { useTestimonialsForm } from './hooks/useTestimonialsForm';

const authLayoutCSS = {
  [AuthFormType.Login]: {
    loginFormCSS: css`
      background: var(--background);
    `,
  },
  [AuthFormType.Registration]: {
    containerCSS: css`
      width: 100%;
    `,
    wrapperCSS: css`
      width: 600px;
    `,
  },
  [AuthFormType.LoginWithPassword]: {
    containerCSS: css`
      width: 290px;
    `,
    wrapperCSS: css`
      width: 100%;
    `,
  },
  [AuthFormType.LoginWithSMSCode]: {
    containerCSS: css`
      width: 290px;
    `,
    wrapperCSS: css`
      width: 100%;
    `,
  },
};

export const TestimonialsFormApp = () => {
  const { formTypes, setFormTypes } = useTestimonialsFormTypes();
  const dispatch = useDispatch();

  const logIn = (data) => dispatch(logInAction(data));
  const setConfirmedPhone = (data) => dispatch(setClientConfirmPhone(data));
  const { formValueCache, saveFormValueCache, resetFormValueCache } =
    useTestimonialsFormCache();
  const currentClient = useSelector(getClientData);
  const unconfirmedPhone = useSelector(getClientUnconfirmedPhone);
  const confirmedPhone = useSelector(getClientPhone);
  const isConfirmedEmail = useSelector(getConfirmedAt);

  const prevConfirmedPhoneParams = useRef<{
    confirmedPhone: string | null;
    unconfirmedPhone: string | null;
  }>({
    confirmedPhone: null,
    unconfirmedPhone: null,
  });

  // const [orderId, setOrderId] = useState<string | undefined>(undefined);
  const [, setOrderId] = useState<string | undefined>(undefined);
  const urlParams = new URLSearchParams(window.location.search);
  const extractedOrderId = urlParams.get('order_id');

  useEffect(() => {
    if (extractedOrderId) {
      setOrderId(extractedOrderId);
    }
  }, [extractedOrderId]);

  const {
    handleTestimonialsCreate,
    setTestimonialsCreateResponse,
    testimonialsCreateResponse,
  } = useTestimonialsCreate();
  // } = useTestimonialsCreate(orderId);
  const { handleLinkClientToTestimonials } = useTestimonialsLink();

  const { handleSubmit, handleSignUp } = useTestimonialsForm({
    currentClient: currentClient
      ? {
          id: currentClient.id ?? 0,
          name: currentClient.name,
          surname: currentClient.surname || '',
          phone: currentClient.phone,
          email: currentClient.email || '',
        }
      : null,
    handleTestimonialsCreate,
    handleLinkClientToTestimonials,
    setFormTypes,
    saveFormValueCache,
    setTestimonialsCreateResponse,
    testimonialsCreateResponse,
    resetFormValueCache,
  });

  // хак, с помощью которого в функциях из хука useTestimonialsForm достается АКТУАЛЬНОЕ значение unconfirmedPhone
  useEffect(() => {
    if (
      currentClient &&
      !unconfirmedPhone &&
      confirmedPhone &&
      !prevConfirmedPhoneParams.current.confirmedPhone &&
      prevConfirmedPhoneParams.current.unconfirmedPhone
    ) {
      handleSignUp({
        id: currentClient.id ?? 0,
        name: currentClient.name,
        surname: currentClient.surname || '',
        phone: currentClient.phone,
        email: currentClient.email || '',
      });
    }

    prevConfirmedPhoneParams.current.confirmedPhone = confirmedPhone;
    prevConfirmedPhoneParams.current.unconfirmedPhone = unconfirmedPhone;
  }, [unconfirmedPhone, confirmedPhone, currentClient, handleSignUp]);

  const isMobile = useSelector(getIsMobile);
  const isWhiteLabel = useSelector(getIsWhitelabel);

  const onCloseAuth = (client) => {
    if (!client) {
      setFormTypes(TestimonialsFormTypes.create);
      return;
    }
    handleSignUp(client);
  };

  const onGetClientType = getClientType(
    !!currentClient,
    isConfirmedEmail,
    unconfirmedPhone,
  );

  function initialFormType() {
    if (currentClient && unconfirmedPhone) {
      return AuthFormType.SetPhoneForConfirm;
    }
    return AuthFormType.Login;
  }

  return (
    <div className="testimonials_form">
      {formTypes === TestimonialsFormTypes.init && (
        <TestimonialsFormInner
          onClick={() => setFormTypes(TestimonialsFormTypes.create)}
        />
      )}
      {formTypes === TestimonialsFormTypes.create && (
        <TestimonialsForm
          initialValues={formValueCache}
          onSubmit={handleSubmit}
        />
      )}
      {formTypes === TestimonialsFormTypes.auth && (
        <TestimonialsAuthWrapper>
          <AuthNew
            isMobile={isMobile}
            isWhiteLabel={isWhiteLabel}
            authLayoutCSS={authLayoutCSS}
            onClose={onCloseAuth}
            onSignUp={logIn}
            onSignIn={handleSignUp}
            onSaveClient={logIn}
            setConfirmedPhone={setConfirmedPhone}
            clientType={onGetClientType}
            initialFormType={initialFormType()}
            pageType={PageType.Testimonials}
          />
        </TestimonialsAuthWrapper>
      )}
      {formTypes === TestimonialsFormTypes.loading && <CommentLoader />}
      {formTypes === TestimonialsFormTypes.done && <TestimonialDone />}
    </div>
  );
};
